// src/components/calendar/CalendarSidebar.jsx
import React, { useState, useEffect } from "react";
import styles from "../../pages/CalendarGeneral.module.css";
import MiniMonth from "./MiniMonth";
import api from "../../api";
import { EVENT_TYPE_COLORS } from "../../constants/eventTypes";
import { withEndedLabel } from "../../utils/contractEnded";

export default function CalendarSidebar({
  date,
  selectedDate,
  onDateChange,
  filters,
  onFiltersChange,
  hideHeader = false,
  refreshTrigger, // ✅ 부모에서 내려주는 trigger 값
}) {
  const [isTypeOpen, setIsTypeOpen] = useState(true);
  const [isPersonalOpen, setIsPersonalOpen] = useState(true);
  const [openPersons, setOpenPersons] = useState({});
  const [isEndedOpen, setIsEndedOpen] = useState(false);   // 계약종료 폴더 — 기본 접힘
  const [leaveBalances, setLeaveBalances] = useState([]);

  // ✅ 직원 잔여시간 불러오기
  const fetchBalances = async () => {
    try {
      const res = await api.get("/calendar/leave-balance/");
      setLeaveBalances(res.data);
    } catch (err) {
      console.error("❌ 휴가 잔여시간 불러오기 실패:", err);
    }
  };

  // ✅ 처음 mount + refreshTrigger 바뀔 때마다 실행
  useEffect(() => {
    fetchBalances();
  }, [refreshTrigger]);

  const toggleType = (key) =>
    onFiltersChange({
      ...filters,
      types: { ...filters.types, [key]: !filters.types[key] },
    });

  const togglePersonalType = (person, type) => {
    onFiltersChange({
      ...filters,
      personalTypes: {
        ...filters.personalTypes,
        [person]: {
          ...filters.personalTypes[person],
          [type]: !(filters.personalTypes[person]?.[type] ?? false),
        },
      },
    });
  };


  const addMonths = (baseDate, months) => {
    const d = new Date(baseDate);
    d.setMonth(d.getMonth() + months);
    return d;
  };
  
  const addYears = (baseDate, years) => {
    const d = new Date(baseDate);
    d.setFullYear(d.getFullYear() + years);
    return d;
  };
  

  // 유형·색·순서는 constants/eventTypes.js 단일 출처(캘린더 렌더와 공유).
  // 여기 목록이 곧 "일정 유형 (전체)" 필터 체크박스 목록이다.
  const typeColors = EVENT_TYPE_COLORS;

  return (
    <>
      {/* 미니 달력 */}
      <div className={styles.miniCalendar}>
        {!hideHeader && (
          <div className={styles.calendarHeader}>
            <button className={styles.navButton} onClick={() => onDateChange(addYears(date, -1))}>
              &laquo;
            </button>
            <button className={styles.navButton} onClick={() => onDateChange(addMonths(date, -1))}>
              &lsaquo;
            </button>
            <div className={styles.monthYear}>
              {date.getFullYear()}년 {date.getMonth() + 1}월
            </div>
            <button className={styles.todayBtn} onClick={() => onDateChange(new Date())}>
              오늘
            </button>
            <button className={styles.navButton} onClick={() => onDateChange(addMonths(date, 1))}>
              &rsaquo;
            </button>
            <button className={styles.navButton} onClick={() => onDateChange(addYears(date, 1))}>
              &raquo;
            </button>
          </div>

        )}
        <MiniMonth value={date} selectedDate={selectedDate} onChange={onDateChange} />
      </div>

      {/* 사용자 검색 */}
      <div className={styles.userSearch}>
        <div className={styles.searchLabel}>사용자 검색</div>
        <input
          className={styles.searchInput}
          placeholder="이름 또는 부서"
          value={filters.search}
          onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
        />
      </div>

      {/* 일정 유형 (전체) */}
      <div className={styles.folder}>
        <div
          className={styles.folderHeader}
          onClick={() => setIsTypeOpen(!isTypeOpen)}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.folderTitle}>
            📂 일정 유형 (전체) {isTypeOpen ? "▲" : "▼"}
          </div>
        </div>

        {isTypeOpen && (
          <div className={styles.folderItems}>
            {Object.keys(typeColors).map((t) => (
              <label key={t} className={styles.folderItem}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={!!filters.types[t]}
                  onChange={() => toggleType(t)}
                />
                <span className={styles.itemLabel}>{t}</span>
                <span
                  className={styles.colorBox}
                  style={{
                    backgroundColor: typeColors[t],
                    border: t === "공지사항" ? "1px solid #ccc" : "1px solid transparent",
                  }}
                ></span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* 일정 유형 (개인) */}
      <div className={styles.folder}>
        <div
          className={styles.folderHeader}
          onClick={() => setIsPersonalOpen(!isPersonalOpen)}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.folderTitle}>
            📂 일정 유형 (개인) {isPersonalOpen ? "▲" : "▼"}
          </div>
        </div>

        {isPersonalOpen && (
          <div className={styles.folderItems}>
            {(() => {
              // 빈 이름·테스트 계정 제외. 계약종료자는 숨기지 않고 아래 폴더로 묶는다 —
              // 과거 일정을 사람별로 켜고 끌 수 있어야 해서. 판정은 서버 is_contract_ended 그대로.
              const people = leaveBalances.filter((person) => {
                const name = person.assignee_display || '';
                if (!name.trim()) return false;
                if (name.includes('테스트')) return false;
                return true;
              });
              const active = people.filter((p) => !p.is_contract_ended);
              const ended = people.filter((p) => p.is_contract_ended);

              const renderPerson = (person) => (
              <div key={person.id} className={styles.personFolder}>
                <div
                  className={styles.personHeader}
                  onClick={() =>        

                    setOpenPersons({
                      ...openPersons,
                      [person.assignee_display]: !openPersons[person.assignee_display],
                    })
                  }
                  style={{ cursor: "pointer", fontWeight: "600", marginTop: "8px" }}
                >
                  {/* ⚠️ assignee_display는 필터 키 — 표기는 렌더링에서만 붙인다 */}
                  📁 {withEndedLabel(person.assignee_display, person.is_contract_ended)}{" "}
                  {openPersons[person.assignee_display] ? "▲" : "▼"}
                </div>

                {openPersons[person.assignee_display] && (
                  <>
                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["행사"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "행사")}
                      />
                      <span className={styles.itemLabel}>행사</span>
                    </label>

                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["업무일정"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "업무일정")}
                      />
                      <span className={styles.itemLabel}>업무일정</span>
                    </label>

                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["근무시간 변동"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "근무시간 변동")}
                      />
                      <span className={styles.itemLabel}>근무시간 변동</span>
                    </label>

                    {/* ✅ 임신기 근로시간 단축 — 근무시간 변동과 같은 취급.
                        이 "일정 유형 (개인)" 목록은 위쪽 (전체)와 달리 유형별 JSX가
                        손으로 나열돼 있어(단일 출처 통합에서 빠진 구간) 함께 추가한다.
                        빠뜨리면 (전체)에서 이 유형을 끈 뒤 사람별로 되살릴 수 없다
                        — 필터 판정이 `typeOk || personalOk`라 개인 항목이 없으면
                        personalOk가 항상 false가 되기 때문이다. */}
                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["임신기 근로시간 단축"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "임신기 근로시간 단축")}
                      />
                      <span className={styles.itemLabel}>임신기 근로시간 단축</span>
                    </label>

                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["연차"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "연차")}
                      />
                      <span className={styles.itemLabel}>
                        연차 (남은시간: {(person.annual_leave_hours ?? 0).toFixed(2)}시간)
                      </span>
                    </label>

                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["반차"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "반차")}
                      />
                      <span className={styles.itemLabel}>
                        반차 (남은시간: {(person.annual_leave_hours ?? 0).toFixed(2)}시간)
                      </span>
                    </label>

                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["대체휴무"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "대체휴무")}
                      />
                      <span className={styles.itemLabel}>
                        대체휴무 (남은시간: {(person.alt_leave_hours ?? 0).toFixed(2)}시간)
                      </span>
                    </label>

                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["특별휴가"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "특별휴가")}
                      />
                      <span className={styles.itemLabel}>
                        특별휴가 (남은시간: {(person.special_leave_hours ?? 0).toFixed(2)}시간)
                      </span>
                    </label>

                    {/* ✅ 경조사휴가 — 별도 부여 휴가라 차감이 없어 남은시간 표기도 없다.
                        (개인) 목록은 손으로 나열돼 있어 함께 추가한다(임신기 근로시간 단축 참고). */}
                    <label className={styles.folderItem} style={{ marginLeft: "16px" }}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={filters.personalTypes?.[person.assignee_display]?.["경조사휴가"] ?? false}
                        onChange={() => togglePersonalType(person.assignee_display, "경조사휴가")}
                      />
                      <span className={styles.itemLabel}>경조사휴가</span>
                    </label>

                  </>
                )}
              </div>
              );

              return (
                <>
                  {active.map(renderPerson)}

                  {/* ✅ 계약종료 폴더 — 기본 접힘, 0명이면 숨김.
                      ⚠️ 접힘은 "표시"만 바꾼다. 체크 상태(filters.personalTypes)는 그대로라
                      접혀 있어도 체크된 계약종료자 일정은 캘린더에 계속 보인다. */}
                  {ended.length > 0 && (
                    <div className={styles.personFolder}>
                      <div
                        className={styles.personHeader}
                        onClick={() => setIsEndedOpen(!isEndedOpen)}
                        style={{ cursor: "pointer", fontWeight: "600", marginTop: "12px", color: "#777", whiteSpace: "nowrap" }}
                      >
                        📁 계약종료 ({ended.length}) {isEndedOpen ? "▲" : "▼"}
                      </div>
                      {isEndedOpen && (
                        <div style={{ marginLeft: "12px" }}>
                          {ended.map(renderPerson)}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </>
  );
}
